import { useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { normalizeManufacturerToLogoName } from "../utils/manufacturerLogoUtils";
import { useEffect, useState } from "react";

export interface ManufacturerLogoEntry {
  light: string | null;
  dark: string | null;
}

export type ManufacturerLogoManifest = Record<string, ManufacturerLogoEntry>;

// React Query for fetching manifest and caching it
const useManufacturerLogoManifest = (baseUrl: string) => {
  return useQuery({
    queryKey: ["manufacturer-logo-manifest", baseUrl],
    queryFn: async (): Promise<ManufacturerLogoManifest> => {
      const res = await fetch(`${baseUrl}/manifest.json`);
      if (!res.ok)
        throw new Error(`Failed to fetch logo manifest: ${res.status}`);
      return res.json();
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

/**
 * Resolves a manufacturer name to a theme-aware logo URL using a remote manifest.
 *
 * Fetches `{baseUrl}/manifest.json` (cached indefinitely via React Query) and looks
 * up the lowercased manufacturer name. Returns the appropriate light or dark filename
 * based on the current MUI theme, falling back to the other variant if one is missing.
 *
 * @param manufacturer - Raw manufacturer name (case-insensitive); `null`/`undefined`/empty yields no logo.
 * @param baseUrl - Base URL where `manifest.json` and logo images are hosted.
 * @returns Logo state: `isLoading`, `isError`, `error`, `src` (full encoded URL or null),
 *          `alt` (normalized name), and `hasLogo` (whether a matching entry was found).
 */
export const useManufacturerLogo = (
  manufacturer: string | undefined | null,
  baseUrl: string,
) => {
  const {
    data: manifest,
    isLoading,
    isError,
    error,
  } = useManufacturerLogoManifest(baseUrl);
  const theme = useTheme();
  const [imgError, setImgError] = useState(false);

  const normalizedManufacturer = normalizeManufacturerToLogoName(manufacturer);

  const logo = normalizedManufacturer
    ? manifest?.[normalizedManufacturer]
    : undefined;

  const filename =
    theme.palette.mode === "light"
      ? (logo?.light ?? logo?.dark)
      : (logo?.dark ?? logo?.light);

  useEffect(() => {
    if (imgError || isError) {
      console.error(
        `Error loading logo for manufacturer "${manufacturer}":`,
        isError ? error : "Image failed to load",
      );
    }
  }, [imgError, isError, error, manufacturer]);

  return {
    isLoading,
    isError,
    error,
    imgError,
    src: filename ? `${baseUrl}/${encodeURIComponent(filename)}` : null,
    alt: normalizedManufacturer,
    hasLogo: !!filename,
    setImgError,
  };
};
